import type { AgentRunStatus, AgentRunType } from './agent'
import { request } from './lib'

export const CHAT_API_PREFIX = '/v1/chat'

export function getBotById(robotId: string) {
  return request.get(`${CHAT_API_PREFIX}/robot/${robotId}`)
}

export function getDefaultBot() {
  return request.get(`${CHAT_API_PREFIX}/robot/default`)
}

export function getBotList() {
  return request.get<BotItem[]>(
    `${CHAT_API_PREFIX}/robot/list?include_system=false`,
  )
}

export function uploadFile2Bot(uploadData: {
  conversation: string
  file_type: string
}) {
  return request.post(`${CHAT_API_PREFIX}/upload`, uploadData)
}

export interface BotItem {
  app_id: string
  flow_id: string
  name: string
  id: number | string
  prompt: string
  description: string
  max_token: number
  followers: number
  last_use_time: string
  create_time: string
  color: string
  model: string
  robot_id: string | number
  uid: number
  type: 'system' | 'AI-FLOW' | 'AI-AGENT'
  avatar: string
  /** 跟 avatar 一样的作用 */
  icon: string
  is_ordinary: boolean // 是否免费
  available: boolean
  deleted: boolean
  workspace_id: string
  welcome_message: string
  config?: Record<string, any>
}

export interface ChatMessage {
  conversation_id: string
  stream?: boolean
  content: string
  filename?: string
  file_type?: string
  action?: 'regenerate' | 'continue'
}

export enum ChatbotRunType {
  MULTIPLE_MODELS = 'MULTIPLE_MODELS',
  AGENT_TESTING = 'AGENT_TESTING',
  AGENT = 'AGENT',
  CHATBOT = 'CHATBOT',
  DINGTALK = 'DINGTALK',
  FEISHU = 'FEISHU',
  WEWORK = 'WEWORK',
}

export interface MessageHistoryParams {
  conversation_id: string
  create_time?: number
  page_number?: number
  page_size?: number
}

// export interface AgentTask {
//   duration_time: number
//   tool_type: string
//   message: string
//   name: string
//   status: string
//   timestamp: number
//   type: string
// }
interface CopilotProgress {
  key: string
  name: string
  icon: string
  progress: number
  message: string
  status: 'SUCCEEDED' | 'FAILED'
}

export interface ChatMessageItem {
  upload_file_id?: string
  file_url?: string | undefined
  is_push: boolean
  create_time: string
  file_name: string
  file_type: string
  id: string
  record_status: 'SUCCEEDED' | 'FAILED'
  response: string
  session_id: string
  user_content: string
  response_ext: {
    content:
      | string
      | { input: Record<string, any>; output: string | Record<string, any> }
    duration_time: number
    status: string
    message: string
    timestamp: number
    group_progress_list?: CopilotProgress[]
    tasks: ChatStream[]
  }
}
interface DefaultChatStream {
  duration_time: number
  status: string
  message: string
  timestamp: number
  name: string
  tool_type?: string
  metadata?: {
    color: string
    icon: string
  }
}
export interface LLMChatStream extends DefaultChatStream {
  content: string
  type: 'TEXT'
}

export interface FunctionCallChatStream extends DefaultChatStream {
  content: { input: Record<string, any>; output: string | Record<string, any> }
  type: 'FUNCTION'
}

export interface ChatResponseStream {
  message: { code: number; success: boolean; message: string; now_time: number }
  type: 'result'
}

export type ChatStream = LLMChatStream | FunctionCallChatStream
export interface MessageHistoryData {
  data_list: ChatMessageItem[]
  page_number: number
  page_size: number
  total_count: number
  total_pages: number
}

export interface SendMessagePayload {
  conversation_id: number | string
  session_id?: string
  /**
   * prompt
   */
  content: string
  filename?: string
  /**
   * 文件类型
   */
  file_type?: string
  /**
   * 是否重新生成
   */
  action?: 'regenerate' | 'continue'

  upload_file_id?: string
  /**
   * 文件传递类型，后端吃该逻辑
   */
  file_action?: 'FILE_URL' | 'FILE_DATA' | undefined
}

export type ChatActionType =
  | 'CONVERSATION'
  | 'SESSION_NEW'
  | 'AGENT_RULE_CHANGE'

export function getMessageHistory(params: MessageHistoryParams) {
  const query = new URLSearchParams(params as Record<string, any>)
  return request.get<MessageHistoryData>(`${CHAT_API_PREFIX}/history?${query}`)
}

export function refreshSession(
  conversation_id: string,
  action_type?: ChatActionType,
) {
  return request.get(
    `${CHAT_API_PREFIX}/session/refresh?conversation_id=${conversation_id}&action_type=${action_type}`,
  )
}

export interface ConversationData {
  conversation_id: string
  robot_avatar: string
  robot_color: string
  robot_icon: string
  robot_id: string
  robot_name: string
  robot_welcome_message: string
  run_type: string
  title: string
  uid: number
  update_time: string
  /** 神奇的字段名，实际是对话的最后记录时间 */
  latest_create_time: string
}

export function getConversationList(
  robot_id?: string,
  run_type?: ChatbotRunType,
) {
  return request.get<ConversationData[]>(
    `${CHAT_API_PREFIX}/conversation/list?robot_id=${robot_id || ''}&run_type=${
      run_type || ''
    }`,
  )
}

export function createConversation(data: {
  robot_id: string
  title: string
  run_type: ChatbotRunType
}) {
  return request.post<{ conversation_id: string; session_id: string }>(
    `${CHAT_API_PREFIX}/conversation/create`,
    data,
  )
}

export function deleteConversation(conversation_id: string) {
  return request.delete(
    `${CHAT_API_PREFIX}/conversation?conversation_id=${conversation_id}`,
  )
}

export function updateConversation(conversation_id: string, title: string) {
  return request.put(`${CHAT_API_PREFIX}/conversation`, {
    conversation_id,
    title,
  })
}

export function getCurrentSession(conversationId: string) {
  return request.get<string>(
    `${CHAT_API_PREFIX}/session/current?conversation_id=${conversationId}`,
  )
}

export function getAgentBot(robot_id: string) {
  return request.get(
    ` ${CHAT_API_PREFIX}/conversation/agent_testing/${robot_id}`,
  )
}

export interface Bot {
  id: string
  app_id: string
  color: string
  icon: string
  name: string
  welcome_message: string
  description: string
}
interface AgentConversationData {
  conversation_id: string
  uid: number
  title: string
  robot_id: string
  update_time: number
  bot: Bot
}

export function getAgentTestConversation(robotId: string) {
  return request.get<AgentConversationData>(
    `${CHAT_API_PREFIX}/conversation/agent_testing/${robotId}`,
  )
}

export interface GenerateTitleParams {
  question: string
  answer: string
  conversation_id: string
  is_main_model?: boolean
}

export function generateTitleFromConversation(params: GenerateTitleParams) {
  return request.post<{ title: string }>(`${CHAT_API_PREFIX}/get_title`, params)
}

export function generateTitleFromDiffConversation(params: GenerateTitleParams) {
  return request.post<{ title: string }>(
    `${CHAT_API_PREFIX}/get_title_for_multiple_models`,
    params,
  )
}

export interface UnreadMessageCountMap {
  robot: Record<string, number>
  conversation: Record<string, number>
}
export function getAgentGlobalUnreadMessageCount() {
  return request.get<UnreadMessageCountMap>(
    `${CHAT_API_PREFIX}/record/count_unread_msgs`,
    {
      noRedirect: true,
      ignoreError: true,
    },
  )
}

export function getAgentGlobalUnreadMessageCountForAgentTest() {
  return request.get<UnreadMessageCountMap>(
    `${CHAT_API_PREFIX}/record/count_agent_testing_unread_msgs`,
    {
      noRedirect: true,
      ignoreError: true,
    },
  )
}

export interface ExportAgentLogsParams {
  agent_id: string
  user_name?: string
  run_type?: AgentRunType
  run_status?: AgentRunStatus
  start_time?: string
  end_time?: string
}

export function exportAgentLogs(body: ExportAgentLogsParams) {
  return request.post<string | null>(
    `${CHAT_API_PREFIX}/conversation/agent_log_export`,
    body,
  )
}
